import React, {useState, useEffect} from 'react';
import {History, Filter, Calendar, Package, MapPin, User} from 'lucide-react';
import {useInventory} from '../hooks/useInventory';
import {InventoryMovement, MovementType} from '../models/inventory.model';
import {getMovementTypeLabel, getMovementIcon} from '../utils/movementTypeLabels';
import Button from '../../../Presentation/components/ui/Button';
import Card from '../../../Presentation/components/ui/Card';
import {AppCardTitle} from '../../../Presentation/components/ui/Typography';
import AppTable, {TableColumn} from '../../../Presentation/components/ui/AppTable';
import AppTableHeaderCell from '../../../Presentation/components/ui/AppTableHeaderCell';
import {formatDate} from '../../../Presentation/utils/dateUtils';
import {useTableExport, useExportConfig} from '../../../Presentation/hooks/useTableExport';
import ExportButton from '../../../Presentation/components/ui/ExportButton';
import {ExportFormat} from '../../../types/export.types';
import {ExportFormatters} from '../../../Presentation/utils/exportFormatters';
import {
  INVENTORY_INPUT_CLASS,
  INVENTORY_ERROR_BOX_CLASS,
  INVENTORY_LOADING_TEXT,
  inventoryEmptyText
} from '../ui/inventoryUi';

interface InventoryMovementPageProps {
  productId?: string;
  initialSerialFilter?: string;
}

interface MovementFilters {
  productId?: string;
  type?: MovementType;
  locationId?: string;
  dateFrom?: string;
  dateTo?: string;
  serialNumber?: string;
}

import {PAGINATION_CONFIG} from '../../../constants/appConfig';

const InventoryMovementPage: React.FC<InventoryMovementPageProps> = ({productId, initialSerialFilter}) => {
  const {isLoading, error, getInventoryMovements} = useInventory();
  const [movements, setMovements] = useState<InventoryMovement[]>([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [filters, setFilters] = useState<MovementFilters>({
    productId,
    type: undefined,
    locationId: undefined,
    dateFrom: undefined,
    dateTo: undefined,
    serialNumber: initialSerialFilter ?? undefined
  });
  const [showFilters, setShowFilters] = useState(!!initialSerialFilter);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (initialSerialFilter) {
      setFilters((prev) => ({...prev, serialNumber: initialSerialFilter}));
      setShowFilters(true);
    }
  }, [initialSerialFilter]);
  const [pageSize, setPageSize] = useState<number>(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);


  // Fetch movements from API with pagination and filters
  useEffect(() => {
    const fetchMovements = async () => {
      try {
        setMovementsLoading(true);
        const apiFilters: any = {};
        if (productId) {apiFilters.product_id = productId;}
        if (filters.type) {apiFilters.type = filters.type;}
        if (filters.locationId) {apiFilters.location_id = filters.locationId;}
        if (filters.dateFrom) {apiFilters.date_from = filters.dateFrom;}
        if (filters.dateTo) {apiFilters.date_to = filters.dateTo;}

        const response = await getInventoryMovements(apiFilters, page, pageSize);

        // Process API response

        // معالجة الاستجابة الجديدة
        if (response && 'items' in response && Array.isArray(response.items)) {
          // النمط الجديد مع items و pagination
          setMovements(response.items);

          if (response.pagination) {
            setTotalPages(response.pagination.totalPages || 1);
            setTotalCount(response.pagination.total || response.items.length);
          } else {
            setTotalPages(1);
            setTotalCount(response.items.length);
          }
        } else if (response && Array.isArray(response)) {
          // النمط القديم - مصفوفة مباشرة (للتوافق مع الإصدارات القديمة)
          setMovements(response);
          setTotalPages(1);
          setTotalCount(response.length);
        } else {
          // استجابة فارغة أو غير صحيحة
          setMovements([]);
          setTotalPages(1);
          setTotalCount(0);
        }
      } catch (error) {
        setMovements([]);
        setTotalPages(1);
        setTotalCount(0);
      } finally {
        setMovementsLoading(false);
      }
    };
    fetchMovements();
  }, [productId, filters.type, filters.locationId, filters.dateFrom, filters.dateTo, page]);

  // Pagination controls
  const goToPage = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Export configuration using new Excel export system
  const {createConfig} = useExportConfig<InventoryMovement>();

  const exportConfig = createConfig([
    {
      key: 'type',
      title: 'نوع الحركة',
      formatter: (value) => getMovementTypeLabel(value as MovementType).label
    },
    {
      key: 'productName',
      title: 'اسم المنتج',
      formatter: (value) => value || 'غير محدد'
    },
    {
      key: 'productBarcode',
      title: 'باركود المنتج',
      formatter: (value) => value || 'غير محدد'
    },
    {
      key: 'quantity',
      title: 'الكمية',
      formatter: (value) => value ? String(value) : 'غير محدد'
    },
    {
      key: 'serialNumbers',
      title: 'الرقم التسلسلي',
      formatter: (value: string[] | undefined) =>
        value && value.length > 0 ? value.join(', ') : '—'
    },
    {
      key: 'fromLocationName',
      title: 'الموقع المصدر',
      formatter: (value) => (value && value !== '-') ? value : 'غير محدد'
    },
    {
      key: 'toLocationName',
      title: 'الموقع الهدف',
      formatter: (value) => (value && value !== '-') ? value : 'غير محدد'
    },
    {
      key: 'performedAt',
      title: 'تاريخ التنفيذ',
      formatter: ExportFormatters.date
    },
    {
      key: 'performedBy',
      title: 'نفذت بواسطة',
      formatter: (value) => value || 'غير محدد'
    }
  ], 'حركات_المخزون', 'تصدير حركات المخزون', 'تصدير جميع حركات المخزون مع التفاصيل الكاملة');

  // Export hook
  const {exportData, isExporting, canExport, recordCount} = useTableExport({
    data: movements,
    config: exportConfig
  });

  // Handle export
  const handleExport = async (format: ExportFormat) => {
    await exportData(format);
  };

  // Define table columns for AppTable
  const columns: TableColumn<InventoryMovement>[] = [
    {
      key: 'type',
      title: <AppTableHeaderCell label="نوع الحركة" />,
      render: (movement) => {
        const typeInfo = getMovementTypeLabel(movement.type);
        return (
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${typeInfo.color}`}>
              {getMovementIcon(movement.type)}
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-gray-900">{typeInfo.label}</span>
              <span className="text-xs text-gray-500 font-arabic">{movement.type}</span>
            </div>
          </div>
        );
      }
    },
    {
      key: 'product',
      title: <AppTableHeaderCell label="المنتج" />,
      render: (movement) => (
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg flex items-center justify-center">
            <Package className="w-5 h-5 text-brand-600" />
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-gray-900 text-sm">{movement.productName}</span>
            <span className="text-xs text-gray-500 font-mono bg-gray-50 px-2 py-1 rounded mt-1">
              {movement.productBarcode}
            </span>
          </div>
        </div>
      )
    },
    {
      key: 'serialNumbers',
      title: <AppTableHeaderCell label="الرقم التسلسلي" />,
      render: (movement) => {
        const serials = movement.serialNumbers;
        const display = serials && serials.length > 0 ? serials.join(', ') : '—';
        return (
          <span className="text-sm font-mono text-gray-800" dir="ltr">
            {display}
          </span>
        );
      }
    },
    {
      key: 'location',
      title: <AppTableHeaderCell label="الموقع" />,
      render: (movement) => (
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-green-50 to-green-100 rounded-lg flex items-center justify-center">
            <MapPin className="w-5 h-5 text-green-600" />
          </div>
          <div className="flex flex-col text-sm">
            {movement.fromLocationName && movement.fromLocationName !== '-' && (
              <div className="mb-1">
                <span className="text-xs text-gray-500">من:</span>
                <span className="font-medium text-gray-900 mr-1">{movement.fromLocationName}</span>
              </div>
            )}
            {movement.toLocationName && movement.toLocationName !== '-' && (
              <div>
                <span className="text-xs text-gray-500">إلى:</span>
                <span className="font-medium text-gray-900 mr-1">{movement.toLocationName}</span>
              </div>
            )}
            {(!movement.fromLocationName || movement.fromLocationName === '-') &&
             (!movement.toLocationName || movement.toLocationName === '-') && (
              <span className="text-gray-400 italic">غير محدد</span>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'date',
      title: <AppTableHeaderCell label="التاريخ" />,
      render: (movement) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg flex items-center justify-center">
            <Calendar className="w-5 h-5 text-purple-600" />
          </div>
          <div className="flex flex-col">
            <span className="font-medium text-gray-900 text-sm">
              {movement.performedAt ? formatDate(movement.performedAt) : '-'}
            </span>
            {movement.performedAt && (
              <span className="text-xs text-gray-500">
                {new Date(movement.performedAt).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'performedBy',
      title: <AppTableHeaderCell label="نفذت بواسطة" />,
      render: (movement) => {
        const isMarketer = (movement.type === 'marketer_sale' || String(movement.type).startsWith('marketer_')) ||
          (movement.fromLocationName && String(movement.fromLocationName).includes('مسوق:'));
        const subtitle = isMarketer ? 'مسوق' : 'مستخدم النظام';
        return (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-50 to-orange-100 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-orange-600" />
            </div>
            <div className="flex flex-col">
              <span className="font-medium text-gray-900 text-sm">{movement.performedBy || 'غير محدد'}</span>
              <span className="text-xs text-gray-500">{subtitle}</span>
            </div>
          </div>
        );
      }
    }
  ];

  const tableLoading = isLoading || movementsLoading;

  // Client-side filter by serial (backend movements API does not support serial param)
  const filteredMovements = filters.serialNumber?.trim()
    ? movements.filter((m) =>
        m.serialNumbers?.some((s) => s && (s === filters.serialNumber || s.includes(filters.serialNumber!)))
      )
    : movements;

  const displayCount = filters.serialNumber?.trim() ? filteredMovements.length : totalCount;

  return (
    <div className="space-y-6">
      {/* Filters Card - header row matches Serials filters pattern */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-brand-500" />
            <AppCardTitle>فلاتر حركة المخزون</AppCardTitle>
          </div>
          <Button
            variant={showFilters ? 'primary' : 'outline'}
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 shrink-0"
          >
            <Filter className="w-4 h-4" />
            {showFilters ? 'إخفاء التصفية' : 'تصفية'}
          </Button>
        </div>
        {showFilters && (
        <div className="flex flex-wrap gap-4 items-end">
          <div className="min-w-[180px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">نوع الحركة</label>
            <select
              value={filters.type || ''}
              onChange={(e) => setFilters({...filters, type: e.target.value as MovementType || undefined})}
              className={INVENTORY_INPUT_CLASS}
            >
              <option value="">جميع الأنواع</option>
              <option value="add_serial">إضافة مخزون بسيريال</option>
              <option value="add_non_serial">إضافة مخزون كمية</option>
              <option value="marketer_delivery">تسليم مسوق</option>
              <option value="marketer_sale">بيع لمسوق</option>
            </select>
          </div>
          <div className="min-w-[140px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">من تاريخ</label>
            <input
              type="date"
              value={filters.dateFrom || ''}
              onChange={(e) => setFilters({...filters, dateFrom: e.target.value || undefined})}
              className={INVENTORY_INPUT_CLASS}
            />
          </div>
          <div className="min-w-[140px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">إلى تاريخ</label>
            <input
              type="date"
              value={filters.dateTo || ''}
              onChange={(e) => setFilters({...filters, dateTo: e.target.value || undefined})}
              className={INVENTORY_INPUT_CLASS}
            />
          </div>
          <div className="min-w-[180px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">الرقم التسلسلي</label>
            <input
              type="text"
              value={filters.serialNumber || ''}
              onChange={(e) => setFilters({...filters, serialNumber: e.target.value || undefined})}
              placeholder="فلترة بالسيريال..."
              className={INVENTORY_INPUT_CLASS}
              dir="ltr"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => { setFilters({productId, type: undefined, locationId: undefined, dateFrom: undefined, dateTo: undefined, serialNumber: undefined}); setPage(1); }}
            className="shrink-0"
          >
            مسح التصفية
          </Button>
        </div>
        )}
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <History className="w-5 h-5 text-brand-500" />
            <AppCardTitle>
              حركات المخزون
              {productId && ' - منتج محدد'}
            </AppCardTitle>
          </div>
          <ExportButton
            onExport={handleExport}
            isExporting={isExporting}
            canExport={canExport}
            recordCount={recordCount}
            showDropdown={true}
          />
        </div>

        {/* Error Display */}
        {error && (
          <div className={`mb-6 ${INVENTORY_ERROR_BOX_CLASS}`}>
            <div className="text-red-700 text-sm">{error}</div>
          </div>
        )}

        {/* Unified Table using AppTable */}
        <div className="mt-6">
          <div className="overflow-x-auto">
            <AppTable
              columns={columns}
              data={filteredMovements}
              rowKey={(movement, index) => `movement-${movement.id}-${index || 0}`}
              emptyText={filters.serialNumber?.trim() ? inventoryEmptyText(`حركات تحتوي على السيريال "${filters.serialNumber}"`) : inventoryEmptyText('حركات مخزون')}
              page={page}
              loading={tableLoading}
              loadingMessage={INVENTORY_LOADING_TEXT}
              pageSize={pageSize}
              totalCount={displayCount}
              hidePagination={!!filters.serialNumber?.trim()}
              onPageChange={goToPage}
              onPageSizeChange={(newPageSize) => {
                setPageSize(newPageSize);
                setPage(1); // Reset to first page when changing page size
              }}
              showPageSizeSelector={true}
              showPaginationInfo={true}
              className="shadow-lg border-0"
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default InventoryMovementPage;
